// DS2 — 메타 줄 공용: 항목마다 <span>, 사이는 « · » 글자(aria-hidden 아님 — 화면 읽기·textContent 에서도 붙지 않게).
// 빈 값(null·undefined·false·'')은 항목째 생략(「—」·0 자리 채움 금지 — 0 을 보이려면 호출부가 문자열로).
import { kstNow } from '@/lib/cl-core/kst';
import { Fragment, type ReactNode } from 'react';

/** tone: period.ts(r) 의 'delay'(주황 700 「일정 지연」) · 'mute'(회색 「2026년 중」). late=true 는 tone 'delay' 와 같다. */
export type MetaItem = string | number | null | undefined | false | { text: string | number; late?: boolean; tone?: 'delay' | 'mute' | null; key?: string };

export const clean = (v: unknown): v is string | number => v !== null && v !== undefined && v !== false && String(v).trim() !== '';

export function metaParts(items?: MetaItem[]): Array<{ text: string; late: boolean; tone: 'delay' | 'mute' | null }> {
  const out: Array<{ text: string; late: boolean; tone: 'delay' | 'mute' | null }> = [];
  for (const it of items ?? []) {
    if (it && typeof it === 'object') {
      if (clean(it.text)) { const tone = it.late ? 'delay' : (it.tone ?? null); out.push({ text: String(it.text).trim(), late: tone === 'delay', tone }); }
    } else if (clean(it)) out.push({ text: String(it).trim(), late: false, tone: null });
  }
  return out;
}

/** 메타 문자열(한 줄 평문). */
export const joinMeta = (items?: MetaItem[], sep = ' · ') => metaParts(items).map((p) => p.text).join(sep);

/** <span>a</span><i> · </i><span>b</span> … — 비면 null. */
export function MetaSpans({ items }: { items?: MetaItem[] }): ReactNode {
  const ps = metaParts(items);
  if (!ps.length) return null;
  return ps.map((p, i) => (
    <Fragment key={i}>
      {i > 0 ? <i> · </i> : null}
      <span className={p.tone === 'delay' ? 'late ds2-tone-delay' : p.tone === 'mute' ? 'ds2-tone-mute' : undefined}>{p.text}</span>
    </Fragment>
  ));
}

export const SR_ONLY = { position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 } as const;

const WD = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * ds2_full 결정 1 — 요일은 «날짜에서 계산». 'YYYY-MM-DD'(또는 ISO 앞 10자)는 달력 날짜 그대로(KST 기준 날짜 문자열 가정),
 * Date 는 KST(UTC+9) 달력으로 환산. 못 읽으면 null.
 */
export function kstDateParts(d: string | Date | null | undefined): { y: number; m: number; d: number; wd: string } | null {
  if (!d) return null;
  let y: number, m: number, dd: number;
  if (typeof d === 'string') {
    const mm = /^(\d{4})-(\d{2})-(\d{2})/.exec(d.trim());
    if (!mm) return null;
    y = +mm[1]; m = +mm[2]; dd = +mm[3];
  } else {
    if (Number.isNaN(d.getTime())) return null;
    const k = kstNow(d.getTime());
    y = k.getUTCFullYear(); m = k.getUTCMonth() + 1; dd = k.getUTCDate();
  }
  const t = new Date(Date.UTC(y, m - 1, dd));
  if (t.getUTCMonth() !== m - 1 || t.getUTCDate() !== dd) return null;
  return { y, m, d: dd, wd: WD[t.getUTCDay()] };
}
